// Editor-managed copy for the hero + each ModuleSection. SWR-backed
// so admin saves reflect on the next public-page focus / reload. The
// public components keep the same defaults in JSX so a missing row,
// a 503, or a slow first load never causes a blank section.

import useSWR from 'swr';
import type { SectionTextRow } from '../db/types';

export type SectionKey =
  | 'hero'
  | 'surveys'
  | 'quiz'
  | 'map'
  | 'proposals'
  | 'candidates';

interface ApiResponse {
  sections: Record<string, SectionTextRow>;
  fetchedAt?: string;
}

const fetcher = async (url: string): Promise<ApiResponse> => {
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as ApiResponse;
};

/** Returns the row for the given section_key, or null while loading
 *  / on error. Components should fall back to their hard-coded JSX
 *  defaults whenever this is null. */
export function useSectionText(
  key: SectionKey,
): SectionTextRow | null {
  const { data } = useSWR<ApiResponse>('/api/section-text', fetcher, {
    revalidateOnFocus: true,
    dedupingInterval: 30_000,
    errorRetryCount: 2,
  });
  return data?.sections?.[key] ?? null;
}

// ─── *emphasis* renderer ──────────────────────────────────────────
//
// Editors write `Survey *Tracker*` in the title field; we turn that
// into <Survey> <em>Tracker</em>. Plain strings without asterisks
// pass through unchanged. No nesting, no escaping needed for the
// single use-case this covers.

import type { ReactNode } from 'react';
import { Fragment } from 'react';

export function renderEmphasis(text: string): ReactNode {
  if (!text || !text.includes('*')) return text;
  const parts = text.split(/\*([^*]+)\*/g);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <em key={i}>{part}</em>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
    </>
  );
}

// ─── Multi-paragraph intro ────────────────────────────────────────
//
// Editor text is a single string with \n\n between paragraphs. The
// existing ModuleSection accepts a ReactNode intro; we render an
// array of <p> elements so the spacing matches what the JSX defaults
// produced.

export function renderIntroParagraphs(text: string): ReactNode {
  if (!text) return null;
  const paras = text
    .split(/\n\s*\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (paras.length <= 1) return text;
  return (
    <>
      {paras.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </>
  );
}

// ─── Resolve (defaults, row) → ModuleSection props ─────────────────
//
// Every component using ModuleSection has hard-coded defaults
// matching what's in the DB seed. This helper merges a possibly-null
// row from the editor on top of those defaults, so the JSX defaults
// always win when the API hasn't resolved yet.

interface ModuleSectionDefaults {
  number: string;
  kicker: string;
  title: ReactNode;
  intro: ReactNode;
}

export function resolveSectionProps(
  defaults: ModuleSectionDefaults,
  row: SectionTextRow | null,
): ModuleSectionDefaults {
  if (!row) return defaults;
  return {
    number: row.number ?? defaults.number,
    kicker: row.kicker ?? defaults.kicker,
    title: row.title ? renderEmphasis(row.title) : defaults.title,
    intro: row.intro ? renderIntroParagraphs(row.intro) : defaults.intro,
  };
}
